import { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Navigation, Loader2, MapPin, X } from "lucide-react";
import { useMapApi } from "@/hooks/useMapApi";

// Pin icon matching MapView pin style (orange accent)
function makePin() {
  const html = `
    <div style="position: relative; width: 28px; height: 36px;">
      <div style="
        position: absolute; top: 0; left: 0;
        width: 28px; height: 28px; border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: #B85C38;
        border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.35);
      "></div>
      <div style="
        position: absolute; top: 5px; left: 5px;
        width: 18px; height: 18px; border-radius: 50%;
        background: white;
      "></div>
    </div>`;
  return L.divIcon({
    html,
    className: "echoes-pin",
    iconSize: [28, 36],
    iconAnchor: [14, 32],
  });
}

function ClickCapture({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], target.zoom || 13, { duration: 0.6 });
  }, [target, map]);
  // First-paint resize fix when inside a modal
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 50);
    const t2 = setTimeout(() => map.invalidateSize(), 350);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [map]);
  return null;
}

/**
 * LocationPicker
 * Renders an inline (in-flow) map for tagging a story's location.
 * - Click anywhere to drop a pin
 * - "Near Me" uses browser geolocation
 * - Type to search OpenStreetMap (forward geocode)
 * - Reverse geocodes on every pin move to surface a human-readable address
 *
 * onChange receives a normalized object: { lat, lng, address, place_name }
 * (or null when cleared).
 */
export default function LocationPicker({ value, onChange, height = 280 }) {
  const api = useMapApi();
  const [pin, setPin] = useState(value || null);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [showHits, setShowHits] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const debounceRef = useRef(null);
  const icon = useRef(makePin()).current;

  // Sync down from controlled value
  useEffect(() => { setPin(value || null); }, [value?.lat, value?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePin = useCallback(async (lat, lng) => {
    const next = { lat, lng };
    setPin(next);
    onChange?.(next);
    setResolving(true);
    const rev = await api.reverseGeocode(lat, lng);
    setResolving(false);
    if (rev && rev.lat) {
      const merged = { lat: rev.lat, lng: rev.lng, address: rev.address, place_name: rev.place_name };
      setPin(merged);
      onChange?.(merged);
    }
  }, [api, onChange]);

  const handleClickMap = useCallback((lat, lng) => {
    updatePin(lat, lng);
  }, [updatePin]);

  const handleNearMe = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setFlyTarget({ lat: latitude, lng: longitude, zoom: 14 });
        updatePin(latitude, longitude);
      },
      () => alert("Couldn't get your location. Allow permissions or click on the map.")
    );
  }, [updatePin]);

  // Debounced forward geocode
  const handleQueryChange = (q) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (q.trim().length < 3) { setHits([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const results = await api.geocode(q);
      setHits(results);
      setSearching(false);
      setShowHits(true);
    }, 350);
  };

  const pickResult = (r) => {
    setQuery(r.display_name);
    setShowHits(false);
    setFlyTarget({ lat: r.lat, lng: r.lng, zoom: 14 });
    updatePin(r.lat, r.lng);
  };

  const clear = () => {
    setPin(null);
    setQuery("");
    onChange?.(null);
  };

  return (
    <div className="space-y-2" data-testid="location-picker">
      {/* Controls */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => hits.length && setShowHits(true)}
            placeholder="Search a city, address, or landmark…"
            className="w-full bg-white border border-[#E5E2D9] pl-8 pr-8 py-2 text-xs focus:outline-none focus:border-[#B85C38]"
            data-testid="picker-geo-search"
          />
          {searching && <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#718096]" />}
          {showHits && hits.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E2D9] shadow-lg z-[1000] max-h-56 overflow-y-auto" data-testid="picker-search-results">
              {hits.map((h) => (
                <button
                  key={`${h.lat},${h.lng}-${h.display_name}`}
                  type="button"
                  onClick={() => pickResult(h)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#F4F1E6] border-b border-[#E5E2D9] last:border-b-0"
                >
                  <MapPin size={11} className="inline mr-2 text-[#B85C38]" />
                  {h.display_name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleNearMe}
          className="btn-ghost text-xs whitespace-nowrap"
          data-testid="picker-near-me"
          title="Use my current location"
        >
          <Navigation size={12} />
          <span className="hidden sm:inline">Near Me</span>
        </button>
        {pin && (
          <button
            type="button"
            onClick={clear}
            className="btn-ghost text-xs text-red-500"
            data-testid="picker-clear"
            title="Clear location"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Map */}
      <div style={{ height, border: "1px solid #E5E2D9", position: "relative" }}>
        <MapContainer
          center={[pin?.lat || 39.0, pin?.lng || -95.5]}
          zoom={pin ? 12 : 4}
          style={{ height: "100%", width: "100%", background: "#F4F1E6" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickCapture onPick={handleClickMap} />
          <FlyTo target={flyTarget} />
          {pin && (
            <Marker
              position={[pin.lat, pin.lng]}
              icon={icon}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  updatePin(lat, lng);
                },
              }}
            />
          )}
        </MapContainer>
        {!pin && (
          <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 text-center pointer-events-none">
            Click the map to drop a pin · or search above
          </div>
        )}
      </div>

      {/* Resolved info */}
      <div className="text-[11px] font-mono text-[#718096] flex items-center gap-2 min-h-[18px]" data-testid="picker-resolved">
        {resolving && <><Loader2 size={11} className="animate-spin" /> Resolving address…</>}
        {!resolving && pin && pin.address && (
          <>
            <MapPin size={11} className="text-[#B85C38]" />
            <span className="truncate">{pin.address}</span>
            <span className="text-[#A0AEC0] ml-auto shrink-0">{pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}</span>
          </>
        )}
        {!resolving && pin && !pin.address && (
          <span>{pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}</span>
        )}
        {!resolving && !pin && <span className="italic">No location selected (optional)</span>}
      </div>
    </div>
  );
}
